import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import PageShell from "../components/PageShell";

export default function AdminOrgUnits() {
  const [units, setUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const selectedUnit = useMemo(
    () => units.find((u) => u.id === selectedUnitId) || null,
    [units, selectedUnitId],
  );

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [uRes, pRes] = await Promise.all([
        axios.get("/api/admin/org-units?include_members=true", {
          withCredentials: true,
        }),
        axios.get("/api/admin/users", { withCredentials: true }),
      ]);
      const nextUnits = Array.isArray(uRes.data) ? uRes.data : [];
      setUnits(nextUnits);
      setUsers(Array.isArray(pRes.data) ? pRes.data : []);
      if (!selectedUnitId && nextUnits.length > 0) {
        setSelectedUnitId(nextUnits[0].id);
      }
    } catch {
      setError("Failed to load org unit mapping data.");
      setUnits([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedUnit) {
      setSelectedMembers([]);
      return;
    }
    const ids = (selectedUnit.members || []).map((m) => m.id);
    setSelectedMembers(ids);
  }, [selectedUnit]);

  const onCreate = async () => {
    setError("");
    setNotice("");
    if (!code.trim() || !name.trim()) {
      setError("Code and name are required.");
      return;
    }
    try {
      const res = await axios.post(
        "/api/admin/org-units",
        { code: code.trim(), name: name.trim() },
        { withCredentials: true },
      );
      setNotice("Org unit created.");
      setCode("");
      setName("");
      await loadAll();
      if (res?.data?.id) setSelectedUnitId(res.data.id);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to create org unit.");
    }
  };

  const onSaveMembers = async () => {
    if (!selectedUnitId) return;
    setError("");
    setNotice("");
    try {
      await axios.post(
        `/api/admin/org-units/${selectedUnitId}/members`,
        { user_ids: selectedMembers },
        { withCredentials: true },
      );
      setNotice("Membership updated.");
      await loadAll();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to update members.");
    }
  };

  const onImport = async () => {
    if (!importFile) return;
    setImportBusy(true);
    setError("");
    setNotice("");
    setImportResult(null);
    try {
      const form = new FormData();
      form.append("file", importFile);
      const res = await axios.post("/api/admin/org-units/import", form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data || null);
      setNotice("B360U mapping import completed.");
      await loadAll();
    } catch (e) {
      setError(e?.response?.data?.error || "Import failed.");
    } finally {
      setImportBusy(false);
    }
  };

  const toggleMember = (userId) => {
    setSelectedMembers((prev) =>
      prev.includes(userId) ? prev.filter((x) => x !== userId) : [...prev, userId],
    );
  };

  return (
    <PageShell
      title="B360U Mapping"
      subtitle="Manage org units and member mapping for team-level kudos fan-out."
    >
      {error ? <div className="pill edAlert edAlert--error">{error}</div> : null}
      {notice ? <div className="pill edAlert edAlert--success">{notice}</div> : null}

      {loading ? (
        <div className="card">
          <div className="card__body">Loading...</div>
        </div>
      ) : (
        <div className="grid-2">
          <div className="card">
            <div className="card__header">
              <h2 className="card__title">Org units</h2>
            </div>
            <div className="card__body" style={{ display: "grid", gap: 10 }}>
              {units.length === 0 ? (
                <div className="edEmpty">No org units yet.</div>
              ) : (
                units.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => setSelectedUnitId(u.id)}
                    style={{
                      justifyContent: "space-between",
                      borderColor: selectedUnitId === u.id ? "var(--neutral-400)" : undefined,
                    }}
                  >
                    <span>{u.name} ({u.code})</span>
                    <span className="pill">{u.member_count} members</span>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="card">
            <div className="card__header">
              <h2 className="card__title">Create org unit</h2>
            </div>
            <div className="card__body" style={{ display: "grid", gap: 10 }}>
              <div className="field">
                <label className="label">Code</label>
                <input className="input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="B360U-AI" />
              </div>
              <div className="field">
                <label className="label">Name</label>
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Zyft AI team" />
              </div>
              <button type="button" className="btn btn--primary" onClick={onCreate}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedUnit ? (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card__header">
            <h2 className="card__title">Members - {selectedUnit.name}</h2>
          </div>
          <div className="card__body" style={{ display: "grid", gap: 8 }}>
            {users.map((u) => (
              <label key={u.id} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selectedMembers.includes(u.id)}
                  onChange={() => toggleMember(u.id)}
                />
                <span>{u.name || u.email} ({u.email})</span>
              </label>
            ))}
            <div style={{ marginTop: 10 }}>
              <button type="button" className="btn btn--primary" onClick={onSaveMembers}>
                Save Members
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card__header">
          <h2 className="card__title">Bulk import (CSV)</h2>
          <p className="card__subtitle">
            Columns: <code>org_unit_code</code>, <code>org_unit_name</code>, <code>user_email</code>
          </p>
        </div>
        <div className="card__body" style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <a
              className="btn btn--secondary"
              href="/api/admin/org-units/import-template"
              style={{ textDecoration: "none" }}
            >
              Download Template
            </a>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              disabled={importBusy}
            />
            <button
              type="button"
              className="btn btn--primary"
              onClick={onImport}
              disabled={importBusy || !importFile}
            >
              {importBusy ? "Importing..." : "Import CSV"}
            </button>
          </div>
          <div className="edReadOnly">
            Tip: download the template, fill rows, then upload.
          </div>

          {importResult ? (
            <div className="edReadOnly">
              Created units: {importResult.created_units || 0}, memberships added:{" "}
              {importResult.added_memberships || 0}, errors:{" "}
              {(importResult.errors || []).length}
            </div>
          ) : null}
          {importResult?.errors?.length ? (
            <div className="edTableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {importResult.errors.map((er, idx) => (
                    <tr key={idx}>
                      <td>{er.line || "-"}</td>
                      <td>{er.error || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
}
